import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { homestayAPI, facilitiesAPI, locationAPI, subLocationAPI, exportAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  ExternalLink,
  Trash2,
  Image,
  Video,
  Loader2,
  X,
  Star,
  MapPin,
  Upload,
  Download,
  Search,
  FileDown,
  CalendarDays,
  RefreshCw,
  Wrench,
  ImageOff,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Utensils,
} from "lucide-react";
import axios from "axios";
import { AvailabilityCalendar } from "@/components/AvailabilityCalendar";
import { 
  SEOValidationPanel, 
  generateMetaTitle, 
  generateMetaDescription, 
  generateSlug 
} from "@/components/SEOHelper";
import ImageUpload from "@/components/ImageUpload";
import RichTextEditor from "@/components/RichTextEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const AdminHomestays = () => {
  const { token } = useAuth();
  const [homestays, setHomestays] = useState([]);
  const [locations, setLocations] = useState([]);
  const [subLocations, setSubLocations] = useState([]);
  const [filteredSubLocations, setFilteredSubLocations] = useState([]);
  const [importingGoogle, setImportingGoogle] = useState(null);
  const [extractingPlaceId, setExtractingPlaceId] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [calendarHomestay, setCalendarHomestay] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingHomestay, setEditingHomestay] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterPublished, setFilterPublished] = useState("all");
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkImportingImages, setBulkImportingImages] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  
  // Google data import state
  const [showGoogleImportModal, setShowGoogleImportModal] = useState(false);
  const [importingGoogleData, setImportingGoogleData] = useState(false);
  const [googleImportResult, setGoogleImportResult] = useState(null);
  const [importOptions, setImportOptions] = useState({ 
    images: true, 
    reviews: true,
    price: true,
    amenities: true
  });
  const [importProgress, setImportProgress] = useState({
    current: 0,
    total: 0,
    currentName: '',
    success: 0,
    failed: 0,
    details: []
  });
  const [isImportCancelled, setIsImportCancelled] = useState(false);
  const cancelledRef = useRef(false); // Use ref to track cancellation in async loop

  // Fix Images Modal State
  const [showFixImagesModal, setShowFixImagesModal] = useState(false);
  const [fixImagesTab, setFixImagesTab] = useState('cleanup'); // 'cleanup', 'reimport', or 'migrate'
  const [fixImagesLoading, setFixImagesLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [reimportResult, setReimportResult] = useState(null);
  const [reimportLimit, setReimportLimit] = useState(50);
  const [reimportProgress, setReimportProgress] = useState({ current: 0, total: 0 });
  const [useGoogleApi, setUseGoogleApi] = useState(true); // Default to Google API

  // R2 Migration State
  const [migrateR2Loading, setMigrateR2Loading] = useState(false);
  const [migrateR2Result, setMigrateR2Result] = useState(null);
  const [migrateR2Limit, setMigrateR2Limit] = useState(100);

  // CSV Import State
  const [csvImportLoading, setCsvImportLoading] = useState(false);
  const [csvImportResult, setCsvImportResult] = useState(null);

  // Form state
  const [form, setForm] = useState({
    name: "",
    mobile_number: "",
    owner_email: "",
    owner_whatsapp: "",
    address: "",
    website: "",
    description: "",
    latitude: "",
    longitude: "",
    meal_included: false,
    breakfast_included: false,
    meal_packages: [],
    facilities: [],
    is_featured: false,
    is_published: true, // Default to published
    check_in_time: "14:00",
    check_out_time: "11:00",
    google_place_id: "",
    google_business_url: "",
    // Pricing
    price_per_night: "",
    price_per_person: "",
    price_currency: "INR",
    // OTA Booking Links
    makemytrip_url: "",
    goibibo_url: "",
    booking_com_url: "",
    agoda_url: "",
    airbnb_url: "",
    other_booking_url: "",
    // Location
    location_id: "",
    sub_location_id: "",
    // SEO Fields
    slug: "",
    meta_title: "",
    meta_description: "",
    canonical_url: "",
    og_image: "",
  });

  // File upload state
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showVideoUpload, setShowVideoUpload] = useState(false);
  const [uploadingHomestay, setUploadingHomestay] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchHomestays();
    fetchFacilities();
    fetchLocations();
    fetchSubLocations();
  }, []);

  // Filter sub-locations when location_id changes
  useEffect(() => {
    if (form.location_id) {
      const filtered = subLocations.filter(sl => sl.location_id === form.location_id);
      setFilteredSubLocations(filtered);
    } else {
      setFilteredSubLocations([]);
    }
  }, [form.location_id, subLocations]);

  const fetchHomestays = async () => {
    try {
      // Fetch up to 2000 homestays to show all in admin (including unpublished)
      const response = await homestayAPI.getAll({ limit: 2000, include_unpublished: true });
      setHomestays(response.data);
    } catch (error) {
      toast.error("Failed to fetch homestays");
    } finally {
      setLoading(false);
    }
  };

  const fetchFacilities = async () => {
    try {
      const response = await facilitiesAPI.getAll();
      setFacilities(response.data.facilities);
    } catch (error) {
      console.error("Error fetching facilities:", error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationAPI.getAll();
      setLocations(response.data);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const fetchSubLocations = async () => {
    try {
      const response = await subLocationAPI.getAll();
      setSubLocations(response.data);
    } catch (error) {
      console.error("Error fetching sub-locations:", error);
    }
  };

  // Filter homestays based on search query, location, and published status
  const filteredHomestays = homestays.filter((homestay) => {
    // Search filter (name, address, mobile, location)
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      homestay.name?.toLowerCase().includes(searchLower) ||
      homestay.address?.toLowerCase().includes(searchLower) ||
      homestay.mobile_number?.includes(searchQuery) ||
      homestay.location_name?.toLowerCase().includes(searchLower) ||
      homestay.sub_location_name?.toLowerCase().includes(searchLower);
    
    // Location filter
    const matchesLocation = !filterLocation || homestay.location_id === filterLocation;
    
    // Published filter
    const matchesPublished = filterPublished === "all" || 
      (filterPublished === "published" && homestay.is_published !== false) ||
      (filterPublished === "unpublished" && homestay.is_published === false);
    
    return matchesSearch && matchesLocation && matchesPublished;
  });

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredHomestays.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredHomestays.map(h => h.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id) 
        : [...prev, id]
    );
  };

  const handleBulkPublish = async (publish) => {
    if (selectedIds.length === 0) {
      toast.error("No homestays selected");
      return;
    }

    const action = publish ? "publish" : "unpublish";
    if (!window.confirm(`Are you sure you want to ${action} ${selectedIds.length} homestays?`)) {
      return;
    }

    setBulkProcessing(true);
    try {
      const response = await axios.post(
        `${API_URL}/homestays/bulk-status`,
        { ids: selectedIds, is_published: publish },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Successfully ${action}ed ${response.data.updated_count} homestays`);
      setSelectedIds([]);
      fetchHomestays();
    } catch (error) {
      console.error(`Bulk ${action} failed:`, error);
      toast.error(error.response?.data?.detail || `Failed to ${action} homestays`);
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      toast.error("No homestays selected");
      return;
    }

    const confirmMsg = `⚠️ WARNING: This will permanently delete ${selectedIds.length} homestays.\n\nThis action cannot be undone!\n\nAre you sure you want to continue?`;
    if (!window.confirm(confirmMsg)) {
      return;
    }

    // Double confirmation for safety
    const doubleConfirm = window.confirm(`Please confirm again: Delete ${selectedIds.length} homestays permanently?`);
    if (!doubleConfirm) {
      return;
    }

    setBulkProcessing(true);
    try {
      const response = await axios.post(
        `${API_URL}/homestays/bulk-delete`,
        { ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Successfully deleted ${response.data.deleted_count} homestays`);
      setSelectedIds([]);
      fetchHomestays();
    } catch (error) {
      console.error("Bulk delete failed:", error);
      toast.error(error.response?.data?.detail || "Failed to delete homestays");
    } finally {
      setBulkProcessing(false);
    }
  };

  // Bulk import images for selected homestays
  const handleBulkImportImages = async () => {
    if (selectedIds.length === 0) {
      toast.error("No homestays selected");
      return;
    }
    
    setBulkImportingImages(true);
    setBulkImportResult(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/admin/reimport-selected-images`,
        { homestay_ids: selectedIds, use_google_api: useGoogleApi },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000 // 5 min timeout
        }
      );
      
      setBulkImportResult(response.data);
      
      if (response.data.success > 0) {
        toast.success(`Imported images for ${response.data.success} of ${response.data.total_to_process} homestays`);
        fetchHomestays();
      } else {
        toast.info("No images could be found for the selected homestays");
      }
    } catch (error) {
      console.error("Bulk image import failed:", error);
      toast.error(error.response?.data?.detail || "Failed to import images");
    } finally {
      setBulkImportingImages(false);
    }
  };

  // Auto-generate SEO fields based on name, location, and village
  const autoGenerateSEO = useCallback((name, locationId, subLocationId, currentForm) => {
    const locationName = locations.find(l => l.id === locationId)?.name || "";
    const villageName = subLocations.find(s => s.id === subLocationId)?.name || "";
    
    const newMetaTitle = generateMetaTitle(name, villageName, locationName);
    const newMetaDescription = generateMetaDescription(name, villageName, locationName);
    const newSlug = generateSlug(name, villageName, locationName);
    
    return {
      ...currentForm,
      meta_title: newMetaTitle,
      meta_description: newMetaDescription,
      slug: newSlug,
    };
  }, [locations, subLocations]);

  // Handler to regenerate SEO fields
  const handleRegenerateSEO = () => {
    const updatedForm = autoGenerateSEO(form.name, form.location_id, form.sub_location_id, form);
    setForm(updatedForm);
    toast.success("SEO fields regenerated!");
  };

  const resetForm = () => {
    setForm({
      name: "",
      mobile_number: "",
      address: "",
      website: "",
      description: "",
      latitude: "",
      longitude: "",
      meal_included: false,
      breakfast_included: false,
      meal_packages: [],
      facilities: [],
      is_featured: false,
      is_published: true, // Default to published
      check_in_time: "14:00",
      check_out_time: "11:00",
      google_place_id: "",
      google_business_url: "",
      price_per_night: "",
      price_per_person: "",
      price_currency: "INR",
      makemytrip_url: "",
      goibibo_url: "",
      booking_com_url: "",
      agoda_url: "",
      airbnb_url: "",
      other_booking_url: "",
      location_id: "",
      sub_location_id: "",
      slug: "",
      meta_title: "",
      meta_description: "",
      og_image: "",
    });
    setEditingHomestay(null);
    setFilteredSubLocations([]);
    // Reset Google import state
    setShowGoogleImport(false);
    setGoogleUrl("");
    setImportedImages([]);
    setSelectedImages(new Set());
    setFeaturedImageIndex(0);
  };

  const handleEdit = (homestay) => {
    setEditingHomestay(homestay);
    setForm({
      name: homestay.name,
      mobile_number: homestay.mobile_number,
      owner_email: homestay.owner_email || "",
      owner_whatsapp: homestay.owner_whatsapp || "",
      address: homestay.address,
      website: homestay.website || "",
      description: homestay.description || "",
      latitude: homestay.latitude?.toString() || "",
      longitude: homestay.longitude?.toString() || "",
      meal_included: homestay.meal_included,
      breakfast_included: homestay.breakfast_included,
      meal_packages: homestay.meal_packages || [],
      facilities: homestay.facilities || [],
      is_featured: homestay.is_featured,
      is_published: homestay.is_published !== false, // Default to true for backward compatibility
      check_in_time: homestay.check_in_time || "14:00",
      check_out_time: homestay.check_out_time || "11:00",
      google_place_id: homestay.google_place_id || "",
      google_business_url: homestay.google_business_url || "",
      price_per_night: homestay.price_per_night?.toString() || "",
      price_per_person: homestay.price_per_person?.toString() || "",
      price_currency: homestay.price_currency || "INR",
      makemytrip_url: homestay.makemytrip_url || "",
      goibibo_url: homestay.goibibo_url || "",
      booking_com_url: homestay.booking_com_url || "",
      agoda_url: homestay.agoda_url || "",
      airbnb_url: homestay.airbnb_url || "",
      other_booking_url: homestay.other_booking_url || "",
      location_id: homestay.location_id || "",
      sub_location_id: homestay.sub_location_id || "",
      slug: homestay.slug || "",
      meta_title: homestay.meta_title || "",
      meta_description: homestay.meta_description || "",
      canonical_url: homestay.canonical_url || "",
      og_image: homestay.og_image || "",
    });
    setShowModal(true);
  };

  // Function to extract Place ID from Google URL
  const handleExtractPlaceId = async () => {
    if (!form.google_business_url) {
      toast.error("Please enter a Google Maps or Google Business URL");
      return;
    }

    setExtractingPlaceId(true);
    try {
      const response = await axios.post(`${API_URL}/extract-place-id`, {
        url: form.google_business_url
      });

      if (response.data.success) {
        setForm(prev => ({
          ...prev,
          google_place_id: response.data.place_id,
          // Optionally update other fields if available
          ...(response.data.latitude && !prev.latitude && { latitude: response.data.latitude.toString() }),
          ...(response.data.longitude && !prev.longitude && { longitude: response.data.longitude.toString() }),
        }));
        toast.success(`Place ID extracted: ${response.data.name || response.data.place_id}`);
      } else {
        toast.error(response.data.error || "Failed to extract Place ID");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to extract Place ID from URL");
    } finally {
      setExtractingPlaceId(false);
    }
  };

  // State for Google Import
  const [showGoogleImport, setShowGoogleImport] = useState(false);
  const [googleUrl, setGoogleUrl] = useState("");
  const [importingFromGoogle, setImportingFromGoogle] = useState(false);
  const [importedImages, setImportedImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState(new Set()); // Track selected images
  const [featuredImageIndex, setFeaturedImageIndex] = useState(0); // Featured image index

  // Toggle image selection
  const toggleImageSelection = (idx) => {
    setSelectedImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) {
        newSet.delete(idx);
      } else {
        newSet.add(idx);
      }
      return newSet;
    });
  };

  // Select all images
  const selectAllImages = () => {
    setSelectedImages(new Set(importedImages.map((_, idx) => idx)));
  };

  // Deselect all images
  const deselectAllImages = () => {
    setSelectedImages(new Set());
  };

  // Function to import all data from Google URL
  const handleImportFromGoogle = async () => {
    if (!googleUrl.trim()) {
      toast.error("Please enter a Google Maps or Google Business URL");
      return;
    }

    setImportingFromGoogle(true);
    try {
      const response = await axios.post(
        `${API_URL}/import-from-google-url`,
        { url: googleUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        // Update form with imported data
        setForm(prev => ({
          ...prev,
          name: data.name || prev.name,
          address: data.address || prev.address,
          mobile_number: data.mobile_number || prev.mobile_number,
          website: data.website || prev.website,
          google_place_id: data.google_place_id || prev.google_place_id,
          google_business_url: data.google_business_url || prev.google_business_url,
          latitude: data.latitude ? data.latitude.toString() : prev.latitude,
          longitude: data.longitude ? data.longitude.toString() : prev.longitude,
          price_per_night: data.price || prev.price_per_night,
          check_in_time: data.check_in_time || prev.check_in_time,
          check_out_time: data.check_out_time || prev.check_out_time,
          // Auto-populate OTA booking links
          makemytrip_url: data.makemytrip_url || prev.makemytrip_url,
          goibibo_url: data.goibibo_url || prev.goibibo_url,
          booking_com_url: data.booking_com_url || prev.booking_com_url,
          agoda_url: data.agoda_url || prev.agoda_url,
        }));

        // Store imported images for preview and auto-select first 5
        if (data.images && data.images.length > 0) {
          setImportedImages(data.images);
          // Auto-select first 5 images as featured
          const autoSelected = new Set();
          for (let i = 0; i < Math.min(5, data.images.length); i++) {
            autoSelected.add(i);
          }
          setSelectedImages(autoSelected);
        }

        // Show success message with details
        const details = [];
        if (data.name) details.push(`Name: ${data.name}`);
        if (data.google_rating) details.push(`Rating: ${data.google_rating}⭐ (${data.google_review_count || 0} reviews)`);
        if (data.images?.length) details.push(`${data.images.length} photos found`);
        if (data.makemytrip_url) details.push(`Booking links generated`);
        
        // Check if this is an approximate match
        if (response.data.is_approximate_match) {
          toast.warning(
            <div>
              <p className="font-semibold">⚠️ Approximate Match Found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Found nearby property: <strong>{data.name}</strong>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Please verify this is the correct property. For exact matches, use Google Maps URL instead.
              </p>
            </div>,
            { duration: 8000 }
          );
        } else {
          toast.success(
            <div>
              <p className="font-semibold">✅ Details imported from Google</p>
              <p className="text-sm text-muted-foreground mt-1">{details.join(' • ')}</p>
            </div>
          );
        }

        // Close the import dialog
        setShowGoogleImport(false);
        setGoogleUrl("");
      } else {
        toast.error(response.data.error || "Failed to import data from Google");
      }
    } catch (error) {
      console.error("Google import error:", error);
      toast.error(error.response?.data?.detail || "Failed to import from Google URL");
    } finally {
      setImportingFromGoogle(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.address) {
      toast.error("Please fill in required fields (Name, Address)");
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...form,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        location_id: form.location_id || null,
        price_per_night: form.price_per_night ? parseFloat(form.price_per_night) : null,
        price_per_person: form.price_per_person ? parseFloat(form.price_per_person) : null,
      };

      if (editingHomestay) {
        await homestayAPI.update(editingHomestay.id, data, token);
        toast.success("Homestay updated successfully");
      } else {
        await homestayAPI.create(data, token);
        toast.success("Homestay created successfully");
      }

      setShowModal(false);
      resetForm();
      fetchHomestays();
    } catch (error) {
      toast.error("Failed to save homestay");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this homestay?")) return;

    try {
      await homestayAPI.delete(id, token);
      toast.success("Homestay deleted successfully");
      fetchHomestays();
    } catch (error) {
      toast.error("Failed to delete homestay");
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      await homestayAPI.uploadImages(uploadingHomestay.id, files, token);
      toast.success("Images uploaded successfully");
      setShowImageUpload(false);
      fetchHomestays();
    } catch (error) {
      toast.error("Failed to upload images");
    } finally {
      setUploading(false);
    }
  };

  // State for tracking broken images
  const [brokenImages, setBrokenImages] = useState(new Set());
  const [checkingImages, setCheckingImages] = useState(false);
  const [reimportingSingle, setReimportingSingle] = useState(false);

  // Check if image is broken (returns 404)
  const checkImageBroken = (imgSrc) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => resolve(false);
      img.onerror = () => resolve(true);
      img.src = imgSrc;
    });
  };

  // Check all images for a homestay
  const checkAllImages = async () => {
    if (!uploadingHomestay?.images?.length) return;
    
    setCheckingImages(true);
    const broken = new Set();
    
    for (let i = 0; i < uploadingHomestay.images.length; i++) {
      const imgUrl = getImageUrl(uploadingHomestay.images[i]);
      const isBroken = await checkImageBroken(imgUrl);
      if (isBroken) {
        broken.add(i);
      }
    }
    
    setBrokenImages(broken);
    setCheckingImages(false);
    
    if (broken.size > 0) {
      toast.warning(`Found ${broken.size} broken image(s)`);
    } else {
      toast.success("All images are working!");
    }
  };

  // Delete all broken images
  const handleDeleteBrokenImages = async () => {
    if (brokenImages.size === 0) {
      toast.info("No broken images to delete");
      return;
    }
    
    try {
      const validImages = uploadingHomestay.images.filter((_, idx) => !brokenImages.has(idx));
      
      await axios.put(
        `${API_URL}/homestays/${uploadingHomestay.id}`,
        { images: validImages },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Deleted ${brokenImages.size} broken image(s)`);
      setBrokenImages(new Set());
      fetchHomestays();
      // Update the local state
      setUploadingHomestay(prev => ({ ...prev, images: validImages }));
    } catch (error) {
      console.error("Failed to delete broken images:", error);
      toast.error("Failed to delete broken images");
    }
  };

  // Re-import images for single homestay using Google Places API
  const handleReimportSingleHomestay = async () => {
    if (!uploadingHomestay) return;
    
    setReimportingSingle(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/reimport-single-homestay-images/${uploadingHomestay.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.images_imported > 0) {
        toast.success(`Imported ${response.data.images_imported} new images from Google Places API`);
        fetchHomestays();
        // Refresh the modal with updated data
        const updatedHomestay = await axios.get(`${API_URL}/homestays/${uploadingHomestay.id}`);
        setUploadingHomestay(updatedHomestay.data);
        setBrokenImages(new Set());
      } else {
        toast.info("No images found on Google Places for this homestay");
      }
    } catch (error) {
      console.error("Failed to reimport images:", error);
      toast.error(error.response?.data?.detail || "Failed to reimport images");
    } finally {
      setReimportingSingle(false);
    }
  };

  // Reset broken images state when modal opens
  useEffect(() => {
    if (showImageUpload && uploadingHomestay) {
      setBrokenImages(new Set());
      // Auto-check images when modal opens
      setTimeout(() => checkAllImages(), 500);
    }
  }, [showImageUpload, uploadingHomestay?.id]);

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      await homestayAPI.uploadVideo(uploadingHomestay.id, file, token);
      toast.success("Video uploaded successfully");
      setShowVideoUpload(false);
      fetchHomestays();
    } catch (error) {
      toast.error("Failed to upload video");
    } finally {
      setUploading(false);
    }
  };

  const handleImportGoogleData = async (homestay) => {
    if (!homestay.google_place_id) {
      toast.error("Please set a Google Place ID first");
      return;
    }

    setImportingGoogle(homestay.id);
    try {
      const response = await axios.post(
        `${API_URL}/homestays/${homestay.id}/import-google-data`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        `Imported ${response.data.photos_imported} photos and ${response.data.reviews_imported} reviews from Google`
      );
      fetchHomestays();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import Google data");
    } finally {
      setImportingGoogle(null);
    }
  };

  const handleDeleteImage = async (homestayId, imageIndex) => {
    try {
      await homestayAPI.deleteImage(homestayId, imageIndex, token);
      toast.success("Image deleted");
      fetchHomestays();
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  const handleSetFeaturedImage = async (homestayId, imageUrl) => {
    try {
      await axios.patch(
        `${API_URL}/homestays/${homestayId}/featured-image`,
        { featured_image: imageUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Featured image updated");
      fetchHomestays();
    } catch (error) {
      toast.error("Failed to set featured image");
    }
  };

  const toggleFacility = (facility) => {
    setForm((prev) => ({
      ...prev,
      facilities: prev.facilities.includes(facility)
        ? prev.facilities.filter((f) => f !== facility)
        : [...prev.facilities, facility],
    }));
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${BACKEND_URL}${imagePath}`;
  };

  // Export to CSV function
  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportAPI.homestays(token);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `homestays_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Homestays exported successfully");
    } catch (error) {
      toast.error("Failed to export homestays");
    } finally {
      setExporting(false);
    }
  };

  // Import Google data (images + reviews) for selected homestays - ONE BY ONE with progress
  const handleGoogleDataImport = async () => {
    if (selectedIds.length === 0) {
      toast.error("Please select at least one homestay");
      return;
    }
    
    // DataForSEO can work with name + address, doesn't strictly need google_place_id
    const homestaysToProcess = homestays.filter(
      h => selectedIds.includes(h.id) && (h.google_place_id || h.name)
    );
    
    if (homestaysToProcess.length === 0) {
      toast.error("No valid homestays selected");
      return;
    }
    
    setImportingGoogleData(true);
    setGoogleImportResult(null);
    setIsImportCancelled(false);
    cancelledRef.current = false; // Reset ref
    
    // Initialize progress
    const totalCount = homestaysToProcess.length;
    setImportProgress({
      current: 0,
      total: totalCount,
      currentName: '',
      success: 0,
      failed: 0,
      details: []
    });
    
    let successCount = 0;
    let failedCount = 0;
    const allDetails = [];
    
    // Process one by one
    for (let i = 0; i < homestaysToProcess.length; i++) {
      // Check if cancelled using ref (not state - state would be stale)
      if (cancelledRef.current) {
        break;
      }
      
      const homestay = homestaysToProcess[i];
      
      // Update progress - currently processing
      setImportProgress(prev => ({
        ...prev,
        current: i + 1,
        currentName: homestay.name
      }));
      
      try {
        // Use the unified import endpoint (now uses DataForSEO internally)
        const response = await axios.post(`${API_URL}/admin/import-google-data-single`, {
          homestay_id: homestay.id,
          import_images: importOptions.images,
          import_reviews: importOptions.reviews,
          import_price: importOptions.price,
          import_amenities: importOptions.amenities
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const result = response.data;
        allDetails.push(result);
        
        if (result.status === 'success') {
          successCount++;
        } else {
          failedCount++;
        }
        
        // Update progress with counts
        setImportProgress(prev => ({
          ...prev,
          success: successCount,
          failed: failedCount,
          details: [...prev.details, result]
        }));
        
      } catch (error) {
        // If API call fails, mark as failed and continue to next
        failedCount++;
        const errorResult = {
          homestay_id: homestay.id,
          name: homestay.name,
          status: 'error',
          message: error.response?.data?.detail || 'API Error'
        };
        allDetails.push(errorResult);
        
        setImportProgress(prev => ({
          ...prev,
          failed: failedCount,
          details: [...prev.details, errorResult]
        }));
        
        // Continue to next item - don't break on failure
        continue;
      }
    }
    
    // Final result
    setGoogleImportResult({
      total: totalCount,
      success: successCount,
      failed: failedCount,
      details: allDetails
    });
    
    setImportingGoogleData(false);
    
    if (successCount > 0) {
      toast.success(`Successfully imported data for ${successCount} homestays`);
      fetchHomestays(); // Refresh to show new data
    }
    
    if (failedCount > 0) {
      toast.warning(`${failedCount} homestays failed (skipped)`);
    }
  };

  // Cancel import
  const handleCancelImport = () => {
    cancelledRef.current = true; // Set ref immediately
    setIsImportCancelled(true);
    toast.info("Import cancelled - completing current item...");
  };

  // Fix Images - Cleanup broken images
  const handleCleanupImages = async (dryRun = true) => {
    setFixImagesLoading(true);
    setCleanupResult(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/admin/cleanup-broken-images?dry_run=${dryRun}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCleanupResult(response.data);
      
      if (!dryRun && response.data.homestays_with_broken_images > 0) {
        toast.success(`Cleaned up ${response.data.total_broken_images_removed} broken images from ${response.data.homestays_with_broken_images} homestays`);
        fetchHomestays(); // Refresh the list
      } else if (dryRun) {
        toast.info(`Found ${response.data.homestays_with_broken_images} homestays with broken images`);
      } else {
        toast.success("No broken images found!");
      }
    } catch (error) {
      console.error("Cleanup failed:", error);
      toast.error(error.response?.data?.detail || "Failed to cleanup images");
    } finally {
      setFixImagesLoading(false);
    }
  };

  // Migrate local images to Cloudflare R2
  const handleMigrateToR2 = async (dryRun = true) => {
    setMigrateR2Loading(true);
    if (dryRun) setMigrateR2Result(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/admin/migrate-images-to-r2?dry_run=${dryRun}&limit=${migrateR2Limit}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMigrateR2Result(response.data);
      
      if (!dryRun && response.data.images_uploaded > 0) {
        toast.success(`Migrated ${response.data.images_uploaded} images from ${response.data.migrated} homestays to R2`);
        fetchHomestays();
      } else if (dryRun) {
        if (response.data.migrated > 0) {
          toast.info(`Found ${response.data.migrated} homestays with ${response.data.details.reduce((sum, d) => sum + d.images_migrated, 0)} local images to migrate`);
        } else {
          toast.success("All images are already on R2!");
        }
      }
    } catch (error) {
      console.error("R2 migration failed:", error);
      toast.error(error.response?.data?.detail || "Failed to migrate images to R2");
    } finally {
      setMigrateR2Loading(false);
    }
  };

  // CSV bulk image import
  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setCsvImportLoading(true);
    setCsvImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/admin/bulk-import-image-urls`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 300000 // 5 min timeout for large CSVs
        }
      );
      
      setCsvImportResult(response.data);
      
      if (response.data.success > 0) {
        toast.success(`Imported ${response.data.images_uploaded} images for ${response.data.success} homestays`);
        fetchHomestays();
      } else {
        toast.info("No images were imported");
      }
    } catch (error) {
      console.error("CSV import failed:", error);
      toast.error(error.response?.data?.detail || "Failed to import from CSV");
    } finally {
      setCsvImportLoading(false);
      e.target.value = ''; // Reset file input
    }
  };

  // Fix Images - Bulk reimport images
  const handleBulkReimport = async () => {
    setFixImagesLoading(true);
    setReimportResult(null);
    setReimportProgress({ current: 0, total: reimportLimit });
    
    try {
      const response = await axios.post(
        `${API_URL}/admin/bulk-reimport-images?limit=${reimportLimit}&skip_with_images=true&use_google_api=${useGoogleApi}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setReimportResult(response.data);
      
      if (response.data.success > 0) {
        toast.success(`Successfully imported images for ${response.data.success} homestays using ${response.data.source === 'google' ? 'Google Places API' : 'DataForSEO'}`);
        fetchHomestays(); // Refresh the list
      } else {
        toast.info("No homestays needed image reimport");
      }
    } catch (error) {
      console.error("Reimport failed:", error);
      toast.error(error.response?.data?.detail || "Failed to reimport images");
    } finally {
      setFixImagesLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-4 sm:space-y-6 overflow-x-hidden" data-testid="admin-homestays">
        {/* Header */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h1
                className="text-xl sm:text-2xl font-semibold text-foreground mb-1"
               
              >
                Homestays
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Manage your homestay listings
              </p>
            </div>
          </div>
          
          {/* Action Buttons - Responsive grid */}
          <div className="flex flex-wrap gap-2 mt-3 sm:mt-0">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exporting}
              className="rounded-full text-xs"
              data-testid="export-homestays-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <FileDown className="w-4 h-4 mr-1" />
              )}
              Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowGoogleImportModal(true)}
              disabled={selectedIds.length === 0}
              className="rounded-full bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 text-xs"
              data-testid="google-import-btn"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Import ({selectedIds.length})
            </Button>
            <Link to="/admin/homestays/bulk-upload">
              <Button variant="outline" size="sm" className="text-xs rounded-full" data-testid="bulk-upload-btn">
                <Upload className="w-4 h-4 mr-1" />
                Bulk
              </Button>
            </Link>
            <Link to="/admin/homestays/google-import" className="hidden md:block">
              <Button variant="outline" size="sm" className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 text-xs rounded-full" data-testid="google-auto-import-btn">
                <MapPin className="w-4 h-4 mr-1" />
                Auto-Import
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowFixImagesModal(true);
                setCleanupResult(null);
                setReimportResult(null);
                setMigrateR2Result(null);
                setCsvImportResult(null);
              }}
              className="rounded-full bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100 text-xs"
              data-testid="fix-images-btn"
            >
              <Wrench className="w-4 h-4 mr-1" />
              Fix Images
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              size="sm"
              className="rounded-full text-xs"
              data-testid="add-homestay-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-soft p-4 overflow-hidden">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by name, address, mobile, location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="homestay-search-input"
              />
            </div>
            
            {/* Filters - Stack on mobile */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              {/* Location Filter */}
              <Select value={filterLocation || "all"} onValueChange={(val) => setFilterLocation(val === "all" ? "" : val)}>
                <SelectTrigger className="w-full sm:w-48" data-testid="location-filter">
                  <SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Published Filter */}
              <Select value={filterPublished} onValueChange={setFilterPublished}>
                <SelectTrigger className="w-full sm:w-40" data-testid="published-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="unpublished">Unpublished</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Results count */}
          <div className="mt-3 text-sm text-muted-foreground">
            Showing {filteredHomestays.length} of {homestays.length} homestays
            {searchQuery && <span className="ml-1">for &quot;{searchQuery}&quot;</span>}
          </div>
          
          {/* Bulk Actions Bar */}
          <div className="mt-4 flex flex-wrap items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <Checkbox
              checked={selectedIds.length === filteredHomestays.length && filteredHomestays.length > 0}
              onCheckedChange={toggleSelectAll}
              data-testid="select-all-checkbox"
            />
            <span className="text-sm font-medium">
              {selectedIds.length > 0 
                ? `${selectedIds.length} selected` 
                : "Select all"}
            </span>
            
            {selectedIds.length > 0 && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleBulkPublish(true)}
                  disabled={bulkProcessing}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="bulk-publish-btn"
                >
                  Publish Selected
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleBulkPublish(false)}
                  disabled={bulkProcessing}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="bulk-unpublish-btn"
                >
                  Unpublish Selected
                </Button>
                <Button
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={bulkProcessing || bulkImportingImages}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="bulk-delete-btn"
                >
                  Delete Selected
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowBulkImportModal(true)}
                  disabled={bulkProcessing || bulkImportingImages}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="bulk-import-images-btn"
                >
                  {bulkImportingImages ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-1" />
                  )}
                  Import Images ({selectedIds.length})
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Homestays List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredHomestays.length > 0 ? (
          <div className="grid gap-4 sm:gap-6">
            {filteredHomestays.map((homestay) => (
              <div
                key={homestay.id}
                className={`bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-soft ${selectedIds.includes(homestay.id) ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                data-testid={`homestay-item-${homestay.id}`}
              >
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                  {/* Selection Checkbox - Top on mobile */}
                  <div className="flex items-center gap-3 sm:absolute sm:left-auto">
                    <Checkbox
                      checked={selectedIds.includes(homestay.id)}
                      onCheckedChange={() => toggleSelect(homestay.id)}
                      data-testid={`select-homestay-${homestay.id}`}
                    />
                    <span className="text-sm text-muted-foreground sm:hidden">Select</span>
                  </div>
                  
                  {/* Image */}
                  <div className="w-full sm:w-48 h-32 sm:h-36 rounded-lg sm:rounded-xl overflow-hidden bg-muted flex-shrink-0">
                    {homestay.images?.[0] ? (
                      <img
                        src={getImageUrl(homestay.images[0])}
                        alt={homestay.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = "https://images.unsplash.com/photo-1664616466598-1f1a394f9009?w=300&h=200&fit=crop";
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="text-lg sm:text-xl font-semibold text-foreground truncate">
                            {homestay.name}
                          </h3>
                          {homestay.is_featured && (
                            <span className="px-2 py-0.5 bg-accent/10 text-accent text-xs font-medium rounded-full whitespace-nowrap">
                              Featured
                            </span>
                          )}
                          {homestay.is_published === false && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full whitespace-nowrap">
                              Draft
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span className="text-sm truncate">{homestay.address}</span>
                        </div>
                      </div>
                      {homestay.average_rating > 0 && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Star className="w-4 h-4 fill-accent text-accent" />
                          <span className="font-semibold">{homestay.average_rating.toFixed(1)}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-3">
                      <span>Phone: {homestay.mobile_number}</span>
                      <span>|</span>
                      <span>Check-in: {homestay.check_in_time || "14:00"}</span>
                      <span>|</span>
                      <span>Check-out: {homestay.check_out_time || "11:00"}</span>
                      {homestay.price_per_night && (
                        <>
                          <span>|</span>
                          <span className="font-semibold text-primary">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: homestay.price_currency || 'INR',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(homestay.price_per_night)}/night
                          </span>
                        </>
                      )}
                      {homestay.price_per_person && (
                        <>
                          <span>|</span>
                          <span className="font-semibold text-[#C86A3A]">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: homestay.price_currency || 'INR',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(homestay.price_per_person)}/person
                          </span>
                        </>
                      )}
                    </div>

                    {/* Facilities */}
                    {homestay.facilities?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {homestay.facilities.slice(0, 5).map((facility, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-full"
                          >
                            {facility}
                          </span>
                        ))}
                        {homestay.facilities.length > 5 && (
                          <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                            +{homestay.facilities.length - 5}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      <a href={`/homestay/${homestay.slug || homestay.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-coconut-green hover:underline px-2 py-1" data-testid={`view-homestay-${homestay.id}`}><ExternalLink className="w-3 h-3" /> View Live</a>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(homestay)}
                        data-testid={`edit-homestay-${homestay.id}`}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCalendarHomestay(homestay)}
                        data-testid={`calendar-${homestay.id}`}
                      >
                        <CalendarDays className="w-4 h-4 mr-1" />
                        Availability
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setUploadingHomestay(homestay);
                          setShowImageUpload(true);
                        }}
                        data-testid={`upload-images-${homestay.id}`}
                      >
                        <Image className="w-4 h-4 mr-1" />
                        Images ({homestay.images?.length || 0})
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setUploadingHomestay(homestay);
                          setShowVideoUpload(true);
                        }}
                        data-testid={`upload-video-${homestay.id}`}
                      >
                        <Video className="w-4 h-4 mr-1" />
                        {homestay.video ? "Replace Video" : "Add Video"}
                      </Button>
                      {homestay.google_place_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleImportGoogleData(homestay)}
                          disabled={importingGoogle === homestay.id}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          data-testid={`import-google-${homestay.id}`}
                        >
                          {importingGoogle === homestay.id ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4 mr-1" />
                          )}
                          Import Google Data
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(homestay.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-homestay-${homestay.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : homestays.length > 0 ? (
          <div className="bg-white rounded-2xl p-12 shadow-soft text-center">
            <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              No Results Found
            </h3>
            <p className="text-muted-foreground mb-6">
              No homestays match your search criteria. Try adjusting your filters.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery("");
                setFilterLocation("all");
                setFilterPublished("all");
              }}
              className="rounded-full"
            >
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 shadow-soft text-center">
            <Image className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              No Homestays Yet
            </h3>
            <p className="text-muted-foreground mb-6">
              Get started by adding your first homestay listing
            </p>
            <Button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Homestay
            </Button>
          </div>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle
                className="text-2xl"
               
              >
                {editingHomestay ? "Edit Homestay" : "Add New Homestay"}
              </DialogTitle>
            </DialogHeader>

            {/* Google Import Section */}
            {!editingHomestay && (
              <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="bg-blue-100 rounded-full p-2">
                    <MapPin className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm text-gray-800">Quick Import from Google</h4>
                    <p className="text-xs text-gray-600 mt-1">
                      Paste a Google Maps, Google Business, or Google Hotels URL to auto-fill details
                    </p>
                    
                    {!showGoogleImport ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2 text-blue-600 border-blue-300 hover:bg-blue-50"
                        onClick={() => setShowGoogleImport(true)}
                        data-testid="show-google-import-btn"
                      >
                        <Search className="w-4 h-4 mr-2" />
                        Import from Google URL
                      </Button>
                    ) : (
                      <div className="mt-3 space-y-2">
                        <Input
                          value={googleUrl}
                          onChange={(e) => setGoogleUrl(e.target.value)}
                          placeholder="Paste Google Maps/Business URL here..."
                          className="h-10 text-sm"
                          data-testid="google-url-input"
                        />
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleImportFromGoogle}
                            disabled={importingFromGoogle || !googleUrl.trim()}
                            className="bg-blue-600 hover:bg-blue-700"
                            data-testid="import-google-btn"
                          >
                            {importingFromGoogle ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Importing...
                              </>
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-2" />
                                Import Details
                              </>
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShowGoogleImport(false);
                              setGoogleUrl("");
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500">
                          Supported: Google Maps, GMB, MakeMyTrip, Goibibo, Booking.com, Agoda & more
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Show imported images preview with selection */}
            {importedImages.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-sm text-green-800 flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      {importedImages.length} Images from Google
                    </h4>
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-2">
                      <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                      <span className="font-medium">Click the star ⭐ to select Featured Image</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-green-600">
                      {selectedImages.size} selected
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs text-green-700 h-7 px-2"
                      onClick={selectAllImages}
                    >
                      Select All
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs text-green-700 h-7 px-2"
                      onClick={deselectAllImages}
                    >
                      Clear
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs text-red-600 h-7 px-2"
                      onClick={() => {
                        setImportedImages([]);
                        setSelectedImages(new Set());
                        setFeaturedImageIndex(0);
                      }}
                    >
                      <X className="w-3 h-3 mr-1" />
                      Remove All
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-3">
                  {importedImages.map((img, idx) => (
                    <div 
                      key={idx} 
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-all duration-200 ${
                        selectedImages.has(idx) 
                          ? featuredImageIndex === idx 
                            ? 'border-amber-500 ring-2 ring-amber-300 scale-[1.02]'
                            : 'border-green-500 ring-2 ring-green-300 scale-[1.02]' 
                          : 'border-transparent hover:border-gray-300'
                      }`}
                      onClick={() => toggleImageSelection(idx)}
                    >
                      <img 
                        src={img} 
                        alt={`Google photo ${idx + 1}`} 
                        className="w-full h-full object-cover" 
                      />
                      {/* Featured Image Star Button - Always visible */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Also select the image when setting as featured
                          if (!selectedImages.has(idx)) {
                            const newSelected = new Set(selectedImages);
                            newSelected.add(idx);
                            setSelectedImages(newSelected);
                          }
                          setFeaturedImageIndex(idx);
                        }}
                        className={`absolute top-2 left-2 w-7 h-7 rounded-full flex items-center justify-center transition-all shadow-md ${
                          featuredImageIndex === idx
                            ? 'bg-amber-500 text-white scale-110'
                            : 'bg-white/90 text-gray-400 hover:text-amber-500 hover:bg-white'
                        }`}
                        title={featuredImageIndex === idx ? 'Featured Image' : 'Click to set as Featured Image'}
                      >
                        <Star className={`w-4 h-4 ${featuredImageIndex === idx ? 'fill-current' : ''}`} />
                      </button>
                      {/* Selection indicator */}
                      <div className={`absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        selectedImages.has(idx)
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'bg-white/80 border-gray-300'
                      }`}>
                        {selectedImages.has(idx) && (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      {/* Image number & Featured badge */}
                      <div className="absolute bottom-1 left-1 flex items-center gap-1">
                        <span className="bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                          {idx + 1}
                        </span>
                        {featuredImageIndex === idx && selectedImages.has(idx) && (
                          <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded font-medium">
                            Featured
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-start gap-4 text-xs text-gray-600">
                  <div className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                    Click to select/deselect images
                  </div>
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                    Click star to set as Featured Image (shown first)
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="homestay-form">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Homestay Name *
                  </label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Enter homestay name"
                    className="h-11"
                    data-testid="form-name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Mobile Number <span className="text-muted-foreground font-normal">(Optional)</span>
                  </label>
                  <Input
                    value={form.mobile_number}
                    onChange={(e) => setForm({ ...form, mobile_number: e.target.value })}
                    placeholder="Enter contact number"
                    className="h-11"
                    data-testid="form-mobile"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Owner Email <span className="text-muted-foreground font-normal">(For inquiry forwarding)</span>
                  </label>
                  <Input
                    type="email"
                    value={form.owner_email}
                    onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                    placeholder="owner@example.com"
                    className="h-11"
                    data-testid="form-owner-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Owner WhatsApp <span className="text-muted-foreground font-normal">(For WhatsApp forwarding)</span>
                  </label>
                  <Input
                    value={form.owner_whatsapp}
                    onChange={(e) => setForm({ ...form, owner_whatsapp: e.target.value })}
                    placeholder="919876543210 (with country code)"
                    className="h-11"
                    data-testid="form-owner-whatsapp"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Address *
                </label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Enter full address"
                  className="h-11"
                  data-testid="form-address"
                />
              </div>

              {/* Location Dropdown */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Location
                </label>
                <Select
                  value={form.location_id}
                  onValueChange={(value) => setForm({ ...form, location_id: value === "none" ? "" : value, sub_location_id: "" })}
                >
                  <SelectTrigger className="h-11" data-testid="form-location">
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Location</SelectItem>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}, {location.state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Select a location to categorize this homestay
                </p>
              </div>

              {/* Sub-Location (Village) Dropdown */}
              {form.location_id && (
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Village / Area
                  </label>
                  <Select
                    value={form.sub_location_id}
                    onValueChange={(value) => setForm({ ...form, sub_location_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger className="h-11" data-testid="form-sub-location">
                      <SelectValue placeholder="Select a village/area" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Village Selected</SelectItem>
                      {filteredSubLocations.map((subLoc) => (
                        <SelectItem key={subLoc.id} value={subLoc.id}>
                          {subLoc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Select a village or area within the location (optional)
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Website
                </label>
                <Input
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://example.com"
                  className="h-11"
                  data-testid="form-website"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Description
                </label>
                <RichTextEditor
                  value={form.description}
                  onChange={(value) => setForm({ ...form, description: value })}
                  placeholder="Describe your homestay..."
                  minHeight="150px"
                />
              </div>

              {/* Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Latitude
                  </label>
                  <Input
                    type="number"
                    step="any"
                    value={form.latitude}
                    onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                    placeholder="e.g., 28.6139"
                    className="h-11"
                    data-testid="form-latitude"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Longitude
                  </label>
                  <Input
                    type="number"
                    step="any"
                    value={form.longitude}
                    onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                    placeholder="e.g., 77.2090"
                    className="h-11"
                    data-testid="form-longitude"
                  />
                </div>
              </div>

              {/* Check-in/Check-out Times */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Check-in Time
                  </label>
                  <Input
                    type="time"
                    value={form.check_in_time}
                    onChange={(e) => setForm({ ...form, check_in_time: e.target.value })}
                    className="h-11"
                    data-testid="form-checkin-time"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Check-out Time
                  </label>
                  <Input
                    type="time"
                    value={form.check_out_time}
                    onChange={(e) => setForm({ ...form, check_out_time: e.target.value })}
                    className="h-11"
                    data-testid="form-checkout-time"
                  />
                </div>
              </div>

              {/* Pricing Section */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  💰 Pricing
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Price per Night
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={form.price_per_night}
                      onChange={(e) => setForm({ ...form, price_per_night: e.target.value })}
                      placeholder="e.g., 2050, 2750, 5000"
                      className="h-11"
                      data-testid="form-price"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Currency
                    </label>
                    <Select
                      value={form.price_currency}
                      onValueChange={(value) => setForm({ ...form, price_currency: value })}
                    >
                      <SelectTrigger className="h-11" data-testid="form-currency">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INR">INR (₹)</SelectItem>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                        <SelectItem value="GBP">GBP (£)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Set a starting price to display on listings. Leave empty if pricing varies.
                </p>

                {/* Per Person Pricing */}
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Per Person Cost (Package)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={form.price_per_person}
                    onChange={(e) => setForm({ ...form, price_per_person: e.target.value })}
                    placeholder="e.g., 500, 800, 1200"
                    className="h-11 bg-white"
                    data-testid="form-price-per-person"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Per person package: Sat morning to Sun morning (includes lunch, high tea, dinner & breakfast)
                  </p>
                </div>
              </div>

              {/* Google Business Integration */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Google Business Integration
                </h3>
                
                <div className="space-y-4">
                  {/* Google Business URL */}
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Google My Business URL
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={form.google_business_url}
                        onChange={(e) => setForm({ ...form, google_business_url: e.target.value })}
                        placeholder="https://g.page/your-business or Google Maps link"
                        className="h-11 flex-1"
                        data-testid="form-google-business-url"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleExtractPlaceId}
                        disabled={extractingPlaceId || !form.google_business_url}
                        className="whitespace-nowrap"
                      >
                        {extractingPlaceId ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Search className="w-4 h-4 mr-2" />
                        )}
                        Extract Place ID
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Paste your Google Business or Google Maps URL to auto-extract the Place ID
                    </p>
                  </div>

                  {/* Google Place ID */}
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Google Place ID
                    </label>
                    <Input
                      value={form.google_place_id}
                      onChange={(e) => setForm({ ...form, google_place_id: e.target.value })}
                      placeholder="e.g., ChIJN1t_tDeuEmsRUsoyG83frY4"
                      className="h-11"
                      data-testid="form-google-place-id"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Auto-extracted from GMB URL or enter manually. Used for Google Reviews integration.
                    </p>
                  </div>
                </div>
              </div>

              {/* OTA Booking Links Section */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Booking Links
                </h3>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-amber-800 font-medium mb-2">
                    ⚠️ Add the exact hotel detail page URL from each platform
                  </p>
                  <div className="text-xs text-amber-700 space-y-1">
                    <p><strong>How to find correct URLs:</strong></p>
                    <ol className="list-decimal ml-4 space-y-0.5">
                      <li>Search for the homestay on MakeMyTrip/Goibibo/Booking.com</li>
                      <li>Open the hotel detail page (not search results)</li>
                      <li>Copy the URL from browser address bar</li>
                    </ol>
                    <div className="mt-2 pt-2 border-t border-amber-200">
                      <p className="font-medium">Example URL formats:</p>
                      <p>✓ <code className="bg-amber-100 px-1 rounded text-[11px]">makemytrip.com/hotels/hotel_name-details-city.html</code></p>
                      <p>✓ <code className="bg-amber-100 px-1 rounded text-[11px]">goibibo.com/hotels/hotel-name-cityid/</code></p>
                      <p>✓ <code className="bg-amber-100 px-1 rounded text-[11px]">booking.com/hotel/in/hotel-name.html</code></p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      MakeMyTrip URL
                    </label>
                    <Input
                      value={form.makemytrip_url}
                      onChange={(e) => setForm({ ...form, makemytrip_url: e.target.value })}
                      placeholder="https://www.makemytrip.com/..."
                      className="h-11"
                      data-testid="form-makemytrip-url"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Goibibo URL
                    </label>
                    <Input
                      value={form.goibibo_url}
                      onChange={(e) => setForm({ ...form, goibibo_url: e.target.value })}
                      placeholder="https://www.goibibo.com/..."
                      className="h-11"
                      data-testid="form-goibibo-url"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Booking.com URL
                    </label>
                    <Input
                      value={form.booking_com_url}
                      onChange={(e) => setForm({ ...form, booking_com_url: e.target.value })}
                      placeholder="https://www.booking.com/..."
                      className="h-11"
                      data-testid="form-booking-url"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Agoda URL
                    </label>
                    <Input
                      value={form.agoda_url}
                      onChange={(e) => setForm({ ...form, agoda_url: e.target.value })}
                      placeholder="https://www.agoda.com/..."
                      className="h-11"
                      data-testid="form-agoda-url"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Airbnb URL
                    </label>
                    <Input
                      value={form.airbnb_url}
                      onChange={(e) => setForm({ ...form, airbnb_url: e.target.value })}
                      placeholder="https://www.airbnb.com/..."
                      className="h-11"
                      data-testid="form-airbnb-url"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Other Booking URL
                    </label>
                    <Input
                      value={form.other_booking_url}
                      onChange={(e) => setForm({ ...form, other_booking_url: e.target.value })}
                      placeholder="https://..."
                      className="h-11"
                      data-testid="form-other-booking-url"
                    />
                  </div>
                </div>
              </div>

              {/* SEO Section */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <Search className="w-5 h-5" />
                    SEO Settings
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRegenerateSEO}
                    disabled={!form.name}
                    className="text-xs"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Auto-Generate All
                  </Button>
                </div>
                
                {/* SEO Validation Panel */}
                <div className="mb-6">
                  <SEOValidationPanel 
                    form={form} 
                    featuredImage={form.og_image}
                    locations={locations}
                    subLocations={subLocations}
                  />
                </div>
                
                <div className="space-y-4">
                  {/* URL Slug */}
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      URL Slug
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={form.slug}
                        onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                        placeholder="e.g., beach-villa-goa"
                        className={`h-11 ${!form.slug ? 'border-red-300' : /^[a-z0-9-]+$/.test(form.slug) ? 'border-green-300' : 'border-amber-300'}`}
                        data-testid="form-slug"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const locationName = locations.find(l => l.id === form.location_id)?.name || "";
                          const villageName = subLocations.find(s => s.id === form.sub_location_id)?.name || "";
                          setForm({ ...form, slug: generateSlug(form.name, villageName, locationName) });
                        }}
                        disabled={!form.name}
                      >
                        Generate
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      URL: /homestay/<span className="font-medium text-primary">{form.slug || 'your-slug-here'}</span>
                    </p>
                  </div>

                  {/* Meta Title */}
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Meta Title
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={form.meta_title}
                        onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
                        placeholder="e.g., Beach Villa Goa | Luxury Homestay"
                        className={`h-11 ${!form.meta_title ? 'border-red-300' : form.meta_title.length >= 30 && form.meta_title.length <= 60 ? 'border-green-300' : 'border-amber-300'}`}
                        data-testid="form-meta-title"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const locationName = locations.find(l => l.id === form.location_id)?.name || "";
                          const villageName = subLocations.find(s => s.id === form.sub_location_id)?.name || "";
                          setForm({ ...form, meta_title: generateMetaTitle(form.name, villageName, locationName) });
                        }}
                        disabled={!form.name}
                      >
                        Generate
                      </Button>
                    </div>
                    <p className={`text-xs mt-1 ${form.meta_title.length >= 30 && form.meta_title.length <= 60 ? 'text-green-600' : form.meta_title.length > 60 ? 'text-red-600' : 'text-muted-foreground'}`}>
                      {form.meta_title.length}/60 characters {form.meta_title.length >= 30 && form.meta_title.length <= 60 ? '✓ Optimal' : form.meta_title.length < 30 ? '(30-60 recommended)' : '⚠ Too long'}
                    </p>
                  </div>

                  {/* Meta Description */}
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Meta Description
                    </label>
                    <div className="flex gap-2">
                      <Textarea
                        value={form.meta_description}
                        onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                        placeholder="A brief description for search engines..."
                        rows={3}
                        className={`${!form.meta_description ? 'border-red-300' : form.meta_description.length >= 100 && form.meta_description.length <= 160 ? 'border-green-300' : 'border-amber-300'}`}
                        data-testid="form-meta-description"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="h-auto"
                        onClick={() => {
                          const locationName = locations.find(l => l.id === form.location_id)?.name || "";
                          const villageName = subLocations.find(s => s.id === form.sub_location_id)?.name || "";
                          setForm({ ...form, meta_description: generateMetaDescription(form.name, villageName, locationName) });
                        }}
                        disabled={!form.name}
                      >
                        Generate
                      </Button>
                    </div>
                    <p className={`text-xs mt-1 ${form.meta_description.length >= 100 && form.meta_description.length <= 160 ? 'text-green-600' : form.meta_description.length > 160 ? 'text-red-600' : 'text-muted-foreground'}`}>
                      {form.meta_description.length}/160 characters {form.meta_description.length >= 100 && form.meta_description.length <= 160 ? '✓ Optimal' : form.meta_description.length < 100 ? '(150-160 recommended)' : '⚠ Too long'}
                    </p>
                  </div>

                  {/* OG Image */}
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Canonical URL</label>
                    <Input
                      value={form.canonical_url}
                      onChange={(e) => setForm({ ...form, canonical_url: e.target.value })}
                      placeholder="https://kokanhomestays.in/homestay/... (leave empty for auto)"
                      data-testid="form-canonical-url"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Leave blank to use the default page URL as canonical</p>
                  </div>

                  {/* OG Image - Original */}
                  <div>
                    <ImageUpload
                      value={form.og_image}
                      onChange={(url) => setForm({ ...form, og_image: url })}
                      token={token}
                      label="Open Graph Image (Featured Image)"
                      placeholder="https://example.com/image.jpg"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Image shown when shared on social media & used in Schema.org (1200x630px recommended)
                    </p>
                  </div>
                </div>
              </div>

              {/* Meal Options */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Utensils className="w-5 h-5" />
                  Meal Package Details
                </h3>

                <div className="flex flex-wrap gap-6 mb-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="meal_included"
                      checked={form.meal_included}
                      onCheckedChange={(checked) =>
                        setForm({ ...form, meal_included: checked })
                      }
                      data-testid="form-meal"
                    />
                    <label htmlFor="meal_included" className="text-sm font-medium cursor-pointer">
                      Meals Included
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="breakfast_included"
                      checked={form.breakfast_included}
                      onCheckedChange={(checked) =>
                        setForm({ ...form, breakfast_included: checked })
                      }
                      data-testid="form-breakfast"
                    />
                    <label htmlFor="breakfast_included" className="text-sm font-medium cursor-pointer">
                      Breakfast Included
                    </label>
                  </div>
                </div>

                {/* Detailed Meal Packages */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-sm font-medium text-amber-900 mb-3">Select meals included in the per-person package:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="meal-packages-grid">
                    {[
                      { id: "breakfast", label: "Breakfast", icon: "☀️" },
                      { id: "lunch", label: "Lunch", icon: "🍛" },
                      { id: "high_tea", label: "High Tea / Snacks", icon: "🍵" },
                      { id: "dinner", label: "Dinner", icon: "🍽️" },
                      { id: "welcome_drink", label: "Welcome Drink", icon: "🥤" },
                      { id: "evening_snacks", label: "Evening Snacks", icon: "🥜" },
                      { id: "packed_lunch", label: "Packed Lunch", icon: "🧳" },
                      { id: "bbq", label: "BBQ / Bonfire Snacks", icon: "🔥" },
                      { id: "veg_only", label: "Veg Only", icon: "🥗" },
                      { id: "veg_nonveg", label: "Veg & Non-Veg", icon: "🍗" },
                      { id: "seafood", label: "Seafood Special", icon: "🦐" },
                      { id: "local_cuisine", label: "Local Konkani Cuisine", icon: "🫕" },
                    ].map(({ id, label, icon }) => {
                      const isSelected = form.meal_packages.includes(id);
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => {
                            const updated = isSelected
                              ? form.meal_packages.filter(p => p !== id)
                              : [...form.meal_packages, id];
                            setForm({ ...form, meal_packages: updated });
                          }}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all text-left ${
                            isSelected
                              ? "bg-[#2F5D3A] text-white border-[#2F5D3A]"
                              : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
                          }`}
                          data-testid={`meal-pkg-${id}`}
                        >
                          <span>{icon}</span>
                          <span>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-amber-700 mt-3">
                    These appear on the homestay detail page under the meal package section.
                  </p>
                </div>
              </div>

              {/* Featured & Publishing toggles */}
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_featured"
                    checked={form.is_featured}
                    onCheckedChange={(checked) =>
                      setForm({ ...form, is_featured: checked })
                    }
                    data-testid="form-featured"
                  />
                  <label htmlFor="is_featured" className="text-sm font-medium cursor-pointer">
                    Featured Listing
                  </label>
                </div>
                
                {/* Publishing Status Toggle */}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_published"
                    checked={form.is_published}
                    onCheckedChange={(checked) =>
                      setForm({ ...form, is_published: checked })
                    }
                    data-testid="form-published"
                  />
                  <label htmlFor="is_published" className="text-sm font-medium cursor-pointer flex items-center gap-1.5">
                    {form.is_published ? (
                      <>
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        Published (visible in sitemap & search)
                      </>
                    ) : (
                      <>
                        <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                        Draft (hidden from sitemap)
                      </>
                    )}
                  </label>
                </div>
              </div>

              {/* Facilities */}
              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">
                  Facilities
                </label>
                <div className="flex flex-wrap gap-2">
                  {facilities.map((facility) => (
                    <button
                      key={facility}
                      type="button"
                      onClick={() => toggleFacility(facility)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        form.facilities.includes(facility)
                          ? "bg-primary text-white"
                          : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                      }`}
                      data-testid={`facility-${facility}`}
                    >
                      {facility}
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="form-submit">
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  {editingHomestay ? "Update Homestay" : "Create Homestay"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Image Upload Modal */}
        <Dialog open={showImageUpload} onOpenChange={(open) => {
          setShowImageUpload(open);
          if (!open) {
            setBrokenImages(new Set());
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Manage Images - {uploadingHomestay?.name}</span>
              </DialogTitle>
            </DialogHeader>

            {/* Action Buttons Bar */}
            <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border">
              <Button
                variant="outline"
                size="sm"
                onClick={checkAllImages}
                disabled={checkingImages || !uploadingHomestay?.images?.length}
                className="text-blue-600 hover:bg-blue-50"
              >
                {checkingImages ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-1" />
                )}
                Check Broken
              </Button>
              
              {brokenImages.size > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteBrokenImages}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete {brokenImages.size} Broken
                </Button>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleReimportSingleHomestay}
                disabled={reimportingSingle}
                className="text-green-600 hover:bg-green-50"
              >
                {reimportingSingle ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-1" />
                )}
                Re-import from Google
              </Button>
              
              {brokenImages.size > 0 && (
                <span className="ml-auto text-sm text-red-600 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  {brokenImages.size} broken image(s) found
                </span>
              )}
            </div>

            {/* Current Images */}
            {uploadingHomestay?.images?.length > 0 ? (
              <div>
                <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  Click the star to set Featured Image | Red border = Broken Image
                </p>
                <div className="grid grid-cols-4 gap-3 mb-4">
                  {uploadingHomestay.images.map((image, index) => {
                    const isFeatured = uploadingHomestay.featured_image === image || 
                      (index === 0 && !uploadingHomestay.featured_image);
                    const isBroken = brokenImages.has(index);
                    return (
                      <div key={index} className={`relative group aspect-square rounded-lg overflow-hidden border-2 ${
                        isBroken 
                          ? 'border-red-500 ring-2 ring-red-300 bg-red-50' 
                          : isFeatured 
                            ? 'border-amber-500 ring-2 ring-amber-300' 
                            : 'border-transparent'
                      }`}>
                        <img
                          src={getImageUrl(image)}
                          alt={`Image ${index + 1}`}
                          className={`w-full h-full object-cover ${isBroken ? 'opacity-50' : ''}`}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        {/* Broken image placeholder */}
                        <div 
                          className="w-full h-full bg-red-100 items-center justify-center text-red-500 hidden"
                          style={{ display: 'none' }}
                        >
                          <ImageOff className="w-8 h-8" />
                        </div>
                        {/* Broken badge */}
                        {isBroken && (
                          <span className="absolute top-1 left-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded font-medium">
                            Broken
                          </span>
                        )}
                        {/* Featured star button */}
                        {!isBroken && (
                          <button
                            onClick={() => handleSetFeaturedImage(uploadingHomestay.id, image)}
                            className={`absolute top-1 left-1 w-7 h-7 rounded-full flex items-center justify-center transition-all shadow-md ${
                              isFeatured
                                ? 'bg-amber-500 text-white'
                                : 'bg-white/90 text-gray-400 hover:text-amber-500 opacity-0 group-hover:opacity-100'
                            }`}
                            title={isFeatured ? 'Featured Image' : 'Set as Featured'}
                          >
                            <Star className={`w-4 h-4 ${isFeatured ? 'fill-current' : ''}`} />
                          </button>
                        )}
                        {/* Featured badge */}
                        {isFeatured && !isBroken && (
                          <span className="absolute bottom-1 left-1 bg-amber-500 text-white text-xs px-2 py-0.5 rounded font-medium">
                            Featured
                          </span>
                        )}
                        {/* Delete button */}
                        <button
                          onClick={() => handleDeleteImage(uploadingHomestay.id, index)}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          title="Delete this image"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ImageOff className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No images yet</p>
                <p className="text-sm">Upload images or re-import from Google</p>
              </div>
            )}

            {/* Upload */}
            <div className="border-2 border-dashed border-border rounded-xl p-8 text-center">
              <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-3">
                Drag and drop images or click to browse
              </p>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
                disabled={uploading}
              />
              <label htmlFor="image-upload">
                <Button asChild disabled={uploading}>
                  <span>
                    {uploading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Select Images
                  </span>
                </Button>
              </label>
            </div>
          </DialogContent>
        </Dialog>

        {/* Video Upload Modal */}
        <Dialog open={showVideoUpload} onOpenChange={setShowVideoUpload}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Upload Video - {uploadingHomestay?.name}</DialogTitle>
            </DialogHeader>

            {/* Current Video */}
            {uploadingHomestay?.video && (
              <div className="mb-4">
                <video
                  src={`${BACKEND_URL}${uploadingHomestay.video}`}
                  controls
                  className="w-full rounded-xl"
                />
              </div>
            )}

            {/* Upload */}
            <div className="border-2 border-dashed border-border rounded-xl p-8 text-center">
              <Video className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-3">
                Upload a video to showcase your homestay
              </p>
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                className="hidden"
                id="video-upload"
                disabled={uploading}
              />
              <label htmlFor="video-upload">
                <Button asChild disabled={uploading}>
                  <span>
                    {uploading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Select Video
                  </span>
                </Button>
              </label>
            </div>
          </DialogContent>
        </Dialog>

        {/* Availability Calendar Modal */}
        {calendarHomestay && (
          <AvailabilityCalendar
            homestayId={calendarHomestay.id}
            homestayName={calendarHomestay.name}
            token={token}
            isOpen={!!calendarHomestay}
            onClose={() => setCalendarHomestay(null)}
            isAdmin={true}
          />
        )}

        {/* Google Data Import Modal */}
        <Dialog open={showGoogleImportModal} onOpenChange={(open) => {
          if (!importingGoogleData) {
            setShowGoogleImportModal(open);
            if (!open) {
              setGoogleImportResult(null);
              setImportProgress({ current: 0, total: 0, currentName: '', success: 0, failed: 0, details: [] });
            }
          }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                Import Google Data
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {!importingGoogleData && !googleImportResult && (
                <>
                  {/* Info Banner */}
                  <div className="rounded-lg p-4 bg-emerald-50 border border-emerald-200">
                    <p className="font-medium text-sm text-emerald-800 mb-1">Import Data via DataForSEO</p>
                    <p className="text-xs text-emerald-700">
                      Fetches ratings, review counts, and business info. Works with name + address (no Google Place ID required).
                    </p>
                  </div>
                  
                  <div className="text-sm">
                    <p className="font-medium">Selected Homestays: {selectedIds.length}</p>
                  </div>
                  
                  {/* Selected homestays list */}
                  <div className="max-h-40 overflow-y-auto border rounded-lg p-2">
                    {homestays.filter(h => selectedIds.includes(h.id)).slice(0, 20).map(h => (
                      <div key={h.id} className="flex items-center justify-between py-1 text-sm">
                        <span className="truncate">{h.name}</span>
                        {h.google_place_id ? (
                          <span className="text-green-600 text-xs">Has Google ID</span>
                        ) : (
                          <span className="text-amber-600 text-xs">Will use name</span>
                        )}
                      </div>
                    ))}
                    {selectedIds.length > 20 && (
                      <p className="text-xs text-muted-foreground text-center py-2">
                        ... and {selectedIds.length - 20} more
                      </p>
                    )}
                  </div>
                  
                  {/* Import options */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Import Options:</p>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex items-center gap-2 text-sm">
                        <Checkbox 
                          checked={importOptions.images}
                          onCheckedChange={(c) => setImportOptions(prev => ({ ...prev, images: c }))}
                        />
                        Images (main)
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <Checkbox 
                          checked={importOptions.reviews}
                          onCheckedChange={(c) => setImportOptions(prev => ({ ...prev, reviews: c }))}
                        />
                        Ratings
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <Checkbox 
                          checked={importOptions.price}
                          onCheckedChange={(c) => setImportOptions(prev => ({ ...prev, price: c }))}
                        />
                        Price (if not set)
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <Checkbox 
                          checked={importOptions.amenities}
                          onCheckedChange={(c) => setImportOptions(prev => ({ ...prev, amenities: c }))}
                        />
                        Work Hours
                      </label>
                    </div>
                  </div>
                </>
              )}
              
              {/* Progress Bar - shown during import */}
              {importingGoogleData && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-800">
                        Processing: {importProgress.current} / {importProgress.total}
                      </span>
                      <span className="text-sm text-blue-600">
                        {Math.round((importProgress.current / importProgress.total) * 100)}%
                      </span>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="w-full bg-blue-200 rounded-full h-3 mb-3">
                      <div 
                        className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                      />
                    </div>
                    
                    {/* Current item */}
                    <p className="text-xs text-blue-700 truncate flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      {importProgress.currentName}
                    </p>
                  </div>
                  
                  {/* Success/Failed counts */}
                  <div className="flex gap-4">
                    <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-600">{importProgress.success}</p>
                      <p className="text-xs text-green-700">Success</p>
                    </div>
                    <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-600">{importProgress.failed}</p>
                      <p className="text-xs text-red-700">Failed</p>
                    </div>
                    <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-gray-600">
                        {importProgress.total - importProgress.current}
                      </p>
                      <p className="text-xs text-gray-700">Remaining</p>
                    </div>
                  </div>
                  
                  {/* Live log */}
                  <div className="max-h-32 overflow-y-auto border rounded-lg p-2 bg-gray-50">
                    {importProgress.details.slice(-5).map((detail, idx) => (
                      <div key={idx} className={`text-xs py-1 flex items-center gap-2 ${detail.status === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                        {detail.status === 'success' ? '✓' : '✗'}
                        <span className="truncate">{detail.name}</span>
                        {detail.status === 'success' && (
                          <span className="text-gray-500 ml-auto">
                            {detail.images_imported > 0 && `${detail.images_imported} img`}
                            {detail.reviews_imported > 0 && ` ${detail.reviews_imported} rev`}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Cancel button */}
                  <Button 
                    variant="outline" 
                    onClick={handleCancelImport}
                    className="w-full text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel Import
                  </Button>
                </div>
              )}
              
              {/* Final Results */}
              {googleImportResult && !importingGoogleData && (
                <div className="space-y-4">
                  <div className={`rounded-lg p-4 ${googleImportResult.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                    {googleImportResult.error ? (
                      <p className="text-red-700 font-medium">{googleImportResult.error}</p>
                    ) : (
                      <div>
                        <p className="font-medium text-green-700 text-lg mb-2">Import Complete!</p>
                        <div className="flex gap-4 text-center">
                          <div className="flex-1">
                            <p className="text-2xl font-bold text-green-600">{googleImportResult.success}</p>
                            <p className="text-xs text-green-700">Success</p>
                          </div>
                          <div className="flex-1">
                            <p className="text-2xl font-bold text-red-600">{googleImportResult.failed}</p>
                            <p className="text-xs text-red-700">Failed</p>
                          </div>
                          <div className="flex-1">
                            <p className="text-2xl font-bold text-gray-600">{googleImportResult.total}</p>
                            <p className="text-xs text-gray-700">Total</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Detailed results */}
                  {googleImportResult.details?.length > 0 && (
                    <div className="max-h-40 overflow-y-auto border rounded-lg p-2">
                      <p className="text-xs font-medium text-gray-700 mb-2">Details:</p>
                      {googleImportResult.details.map((detail, idx) => (
                        <div key={idx} className={`text-xs p-1.5 rounded mb-1 ${detail.status === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-medium truncate">{detail.name}</span>
                            <span className={detail.status === 'success' ? 'text-green-600' : 'text-red-600'}>
                              {detail.status === 'success' ? '✓' : '✗'}
                            </span>
                          </div>
                          {detail.status === 'success' ? (
                            <span className="text-gray-600">
                              {detail.images_imported > 0 && `${detail.images_imported} imgs`}
                              {detail.reviews_imported > 0 && `, ${detail.reviews_imported} reviews`}
                              {detail.price_imported && ', price'}
                              {detail.amenities_imported > 0 && `, ${detail.amenities_imported} amenities`}
                            </span>
                          ) : (
                            <span className="text-red-600">{detail.message}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Action buttons */}
              {!importingGoogleData && (
                <div className="flex justify-end gap-3 pt-2">
                  <Button variant="outline" onClick={() => {
                    setShowGoogleImportModal(false);
                    setGoogleImportResult(null);
                    setImportProgress({ current: 0, total: 0, currentName: '', success: 0, failed: 0, details: [] });
                  }}>
                    {googleImportResult ? 'Close' : 'Cancel'}
                  </Button>
                  {!googleImportResult && (
                    <Button
                      onClick={handleGoogleDataImport}
                      disabled={selectedIds.length === 0 || (!importOptions.images && !importOptions.reviews && !importOptions.price && !importOptions.amenities)}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Start Import
                    </Button>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Fix Images Modal */}
        <Dialog open={showFixImagesModal} onOpenChange={(open) => {
          if (!fixImagesLoading && !migrateR2Loading) {
            setShowFixImagesModal(open);
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-amber-600" />
                Fix Homestay Images
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Tab Navigation */}
              <div className="flex gap-2 border-b">
                <button
                  onClick={() => setFixImagesTab('cleanup')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    fixImagesTab === 'cleanup' 
                      ? 'border-amber-500 text-amber-700' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <ImageOff className="w-4 h-4 inline mr-2" />
                  Cleanup Broken
                </button>
                <button
                  onClick={() => setFixImagesTab('reimport')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    fixImagesTab === 'reimport' 
                      ? 'border-blue-500 text-blue-700' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <RefreshCw className="w-4 h-4 inline mr-2" />
                  Re-import Images
                </button>
                <button
                  onClick={() => setFixImagesTab('migrate')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    fixImagesTab === 'migrate' 
                      ? 'border-green-500 text-green-700' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="migrate-r2-tab"
                >
                  <Upload className="w-4 h-4 inline mr-2" />
                  Migrate to R2
                </button>
                <button
                  onClick={() => setFixImagesTab('csv')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    fixImagesTab === 'csv' 
                      ? 'border-purple-500 text-purple-700' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="csv-import-tab"
                >
                  <FileDown className="w-4 h-4 inline mr-2" />
                  CSV Import
                </button>
              </div>

              {/* Cleanup Tab */}
              {fixImagesTab === 'cleanup' && (
                <div className="space-y-4">
                  {/* Info Banner */}
                  <div className="rounded-lg p-4 bg-amber-50 border border-amber-200">
                    <p className="font-medium text-sm text-amber-800 mb-1">Remove Broken Image References</p>
                    <p className="text-xs text-amber-700">
                      This will scan all homestays and remove references to images that no longer exist on the server. 
                      It also removes any fallback/placeholder images that were accidentally saved.
                    </p>
                  </div>

                  {/* Cleanup Results */}
                  {cleanupResult && (
                    <div className="rounded-lg border p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        {cleanupResult.homestays_with_broken_images > 0 ? (
                          <AlertTriangle className="w-5 h-5 text-amber-500" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        )}
                        <span className="font-medium">
                          {cleanupResult.dry_run ? 'Scan Results' : 'Cleanup Complete'}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-gray-50 rounded p-2">
                          <span className="text-gray-600">Total Homestays:</span>
                          <span className="ml-2 font-medium">{cleanupResult.total_homestays}</span>
                        </div>
                        <div className="bg-amber-50 rounded p-2">
                          <span className="text-amber-700">With Broken Images:</span>
                          <span className="ml-2 font-medium">{cleanupResult.homestays_with_broken_images}</span>
                        </div>
                        <div className="bg-red-50 rounded p-2">
                          <span className="text-red-700">Broken Removed:</span>
                          <span className="ml-2 font-medium">{cleanupResult.total_broken_images_removed}</span>
                        </div>
                        <div className="bg-orange-50 rounded p-2">
                          <span className="text-orange-700">Fallbacks Removed:</span>
                          <span className="ml-2 font-medium">{cleanupResult.fallback_images_removed}</span>
                        </div>
                      </div>

                      {/* Affected homestays list */}
                      {cleanupResult.homestays_updated.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium mb-2">Affected Homestays:</p>
                          <div className="max-h-40 overflow-y-auto border rounded p-2 bg-gray-50">
                            {cleanupResult.homestays_updated.slice(0, 20).map((h, idx) => (
                              <div key={idx} className="flex items-center justify-between py-1 text-xs border-b last:border-0">
                                <span className="truncate flex-1">{h.name}</span>
                                <span className="text-red-600 ml-2">-{h.broken_removed + h.fallback_removed}</span>
                                <span className="text-gray-500 ml-2">({h.images_remaining} left)</span>
                              </div>
                            ))}
                            {cleanupResult.homestays_updated.length > 20 && (
                              <p className="text-xs text-gray-500 pt-2">
                                ...and {cleanupResult.homestays_updated.length - 20} more
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => handleCleanupImages(true)}
                      disabled={fixImagesLoading}
                    >
                      {fixImagesLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4 mr-2" />
                      )}
                      Scan (Preview)
                    </Button>
                    <Button
                      onClick={() => handleCleanupImages(false)}
                      disabled={fixImagesLoading || !cleanupResult || cleanupResult.homestays_with_broken_images === 0}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {fixImagesLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4 mr-2" />
                      )}
                      Remove Broken Images
                    </Button>
                  </div>
                </div>
              )}

              {/* Reimport Tab */}
              {fixImagesTab === 'reimport' && (
                <div className="space-y-4">
                  {/* Info Banner */}
                  <div className="rounded-lg p-4 bg-blue-50 border border-blue-200">
                    <p className="font-medium text-sm text-blue-800 mb-1">Re-import Images from DataForSEO</p>
                    <p className="text-xs text-blue-700">
                      This will find homestays with missing or few images and download fresh images from DataForSEO. 
                      Each homestay can get up to 10 images.
                    </p>
                  </div>

                  {/* Options */}
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Number of homestays to process:
                      </label>
                      <div className="flex items-center gap-3 mt-2">
                        <Input
                          type="number"
                          min="1"
                          max="500"
                          value={reimportLimit}
                          onChange={(e) => setReimportLimit(Math.min(500, Math.max(1, parseInt(e.target.value) || 50)))}
                          className="w-24"
                        />
                        <span className="text-xs text-gray-500">
                          (Max 500 per batch)
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Reimport Results */}
                  {reimportResult && (
                    <div className="rounded-lg border p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        {reimportResult.success > 0 ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-amber-500" />
                        )}
                        <span className="font-medium">Import Complete</span>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div className="bg-gray-50 rounded p-2 text-center">
                          <span className="text-gray-600 block text-xs">Processed</span>
                          <span className="font-bold text-lg">{reimportResult.total_to_process}</span>
                        </div>
                        <div className="bg-green-50 rounded p-2 text-center">
                          <span className="text-green-700 block text-xs">Success</span>
                          <span className="font-bold text-lg text-green-600">{reimportResult.success}</span>
                        </div>
                        <div className="bg-red-50 rounded p-2 text-center">
                          <span className="text-red-700 block text-xs">No Images Found</span>
                          <span className="font-bold text-lg text-red-600">{reimportResult.failed}</span>
                        </div>
                      </div>

                      {/* Successfully Imported Homestays */}
                      {reimportResult.details.filter(d => d.status === 'success').length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium mb-2 text-green-700 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Successfully Imported ({reimportResult.details.filter(d => d.status === 'success').length}):
                          </p>
                          <div className="max-h-32 overflow-y-auto border border-green-200 rounded p-2 bg-green-50">
                            {reimportResult.details.filter(d => d.status === 'success').map((detail, idx) => (
                              <div key={idx} className="flex items-center justify-between py-1.5 text-xs border-b border-green-100 last:border-0">
                                <span className="truncate flex-1 font-medium text-green-800">{detail.name}</span>
                                <span className="text-green-600 font-semibold ml-2 bg-green-100 px-2 py-0.5 rounded">
                                  +{detail.images_imported} images
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Failed/No Images Found */}
                      {reimportResult.details.filter(d => d.status !== 'success').length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium mb-2 text-gray-600 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            No Images Found ({reimportResult.details.filter(d => d.status !== 'success').length}):
                          </p>
                          <div className="max-h-24 overflow-y-auto border rounded p-2 bg-gray-50">
                            {reimportResult.details.filter(d => d.status !== 'success').map((detail, idx) => (
                              <div key={idx} className="flex items-center justify-between py-1 text-xs border-b last:border-0">
                                <span className="truncate flex-1 text-gray-600">{detail.name}</span>
                                <span className="text-gray-500 text-xs ml-2">
                                  {detail.status === 'no_images_found' ? 'Not on DataForSEO' : detail.message || detail.status}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3 pt-2">
                    <Button
                      onClick={handleBulkReimport}
                      disabled={fixImagesLoading}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {fixImagesLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Start Re-import
                    </Button>
                  </div>
                </div>
              )}

              {/* Migrate to R2 Tab */}
              {fixImagesTab === 'migrate' && (
                <div className="space-y-4">
                  {/* Info Banner */}
                  <div className="rounded-lg p-4 bg-green-50 border border-green-200">
                    <p className="font-medium text-sm text-green-800 mb-1">Migrate Local Images to Cloudflare R2</p>
                    <p className="text-xs text-green-700">
                      This will upload all locally-stored images to Cloudflare R2 cloud storage. After migration, images will be served from a fast CDN and will persist across deployments.
                    </p>
                  </div>

                  {/* Options */}
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Homestays per batch:
                      </label>
                      <div className="flex items-center gap-3 mt-2">
                        <Input
                          type="number"
                          min="1"
                          max="500"
                          value={migrateR2Limit}
                          onChange={(e) => setMigrateR2Limit(Math.min(500, Math.max(1, parseInt(e.target.value) || 100)))}
                          className="w-24"
                          data-testid="migrate-r2-limit-input"
                        />
                        <span className="text-xs text-gray-500">
                          (Max 500 per batch. Run multiple times if needed.)
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Migration Results */}
                  {migrateR2Result && (
                    <div className="rounded-lg border p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        {migrateR2Result.migrated > 0 ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : migrateR2Result.total_homestays === 0 ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-amber-500" />
                        )}
                        <span className="font-medium">
                          {migrateR2Result.dry_run ? 'Scan Results (Preview)' : 'Migration Complete'}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <div className="bg-gray-50 rounded p-2 text-center">
                          <span className="text-gray-600 block text-xs">Homestays Found</span>
                          <span className="font-bold text-lg">{migrateR2Result.total_homestays}</span>
                        </div>
                        <div className="bg-green-50 rounded p-2 text-center">
                          <span className="text-green-700 block text-xs">To Migrate</span>
                          <span className="font-bold text-lg text-green-600">{migrateR2Result.migrated}</span>
                        </div>
                        <div className="bg-blue-50 rounded p-2 text-center">
                          <span className="text-blue-700 block text-xs">{migrateR2Result.dry_run ? 'Already on R2' : 'Images Uploaded'}</span>
                          <span className="font-bold text-lg text-blue-600">
                            {migrateR2Result.dry_run ? migrateR2Result.already_migrated : migrateR2Result.images_uploaded}
                          </span>
                        </div>
                        <div className="bg-red-50 rounded p-2 text-center">
                          <span className="text-red-700 block text-xs">Failed</span>
                          <span className="font-bold text-lg text-red-600">{migrateR2Result.failed}</span>
                        </div>
                      </div>

                      {/* Details list */}
                      {migrateR2Result.details.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium mb-2 text-green-700 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            {migrateR2Result.dry_run ? 'Homestays to Migrate' : 'Migrated Homestays'} ({migrateR2Result.details.length}):
                          </p>
                          <div className="max-h-40 overflow-y-auto border border-green-200 rounded p-2 bg-green-50">
                            {migrateR2Result.details.map((detail, idx) => (
                              <div key={idx} className="flex items-center justify-between py-1.5 text-xs border-b border-green-100 last:border-0">
                                <span className="truncate flex-1 font-medium text-green-800">{detail.name}</span>
                                <span className="text-green-600 font-semibold ml-2 bg-green-100 px-2 py-0.5 rounded">
                                  {detail.images_migrated} images
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {migrateR2Result.total_homestays === 0 && (
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <p className="text-sm text-green-700 font-medium">All images are already on Cloudflare R2!</p>
                          <p className="text-xs text-green-600 mt-1">No local images found that need migration.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => handleMigrateToR2(true)}
                      disabled={migrateR2Loading}
                      data-testid="migrate-r2-scan-btn"
                    >
                      {migrateR2Loading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4 mr-2" />
                      )}
                      Scan (Preview)
                    </Button>
                    <Button
                      onClick={() => handleMigrateToR2(false)}
                      disabled={migrateR2Loading || (migrateR2Result && migrateR2Result.total_homestays === 0)}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="migrate-r2-run-btn"
                    >
                      {migrateR2Loading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Migrate to R2
                    </Button>
                  </div>
                </div>
              )}

              {/* CSV Import Tab */}
              {fixImagesTab === 'csv' && (
                <div className="space-y-4">
                  {/* Info Banner */}
                  <div className="rounded-lg p-4 bg-purple-50 border border-purple-200">
                    <p className="font-medium text-sm text-purple-800 mb-1">Bulk Import Images via CSV</p>
                    <p className="text-xs text-purple-700">
                      Upload a CSV file with image URLs. Each image will be downloaded, optimized (resized to 1200px, compressed), and stored on Cloudflare R2.
                    </p>
                  </div>

                  {/* CSV Format Guide */}
                  <div className="rounded-lg border p-4 bg-gray-50">
                    <p className="text-sm font-medium mb-2">CSV Format:</p>
                    <div className="bg-white rounded p-3 font-mono text-xs border">
                      <p className="text-gray-500">homestay_name,image_url</p>
                      <p>Sunny Beach Villa,https://example.com/photo1.jpg</p>
                      <p>Sunny Beach Villa,https://example.com/photo2.jpg</p>
                      <p>Ocean View Resort,https://example.com/photo3.jpg</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Columns: <code className="bg-gray-200 px-1 rounded">homestay_name</code> or <code className="bg-gray-200 px-1 rounded">homestay_id</code> + <code className="bg-gray-200 px-1 rounded">image_url</code>. Multiple rows per homestay are supported.
                    </p>
                  </div>

                  {/* File Upload */}
                  <div className="flex items-center gap-3">
                    <label
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                        csvImportLoading 
                          ? 'border-gray-300 bg-gray-50 cursor-not-allowed' 
                          : 'border-purple-300 hover:border-purple-500 hover:bg-purple-50'
                      }`}
                      data-testid="csv-import-file-input"
                    >
                      {csvImportLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                      ) : (
                        <Upload className="w-5 h-5 text-purple-600" />
                      )}
                      <span className="text-sm font-medium text-purple-700">
                        {csvImportLoading ? 'Importing...' : 'Choose CSV File'}
                      </span>
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleCsvImport}
                        disabled={csvImportLoading}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {/* Results */}
                  {csvImportResult && (
                    <div className="rounded-lg border p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        {csvImportResult.success > 0 ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-amber-500" />
                        )}
                        <span className="font-medium">Import Complete</span>
                      </div>
                      
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <div className="bg-gray-50 rounded p-2 text-center">
                          <span className="text-gray-600 block text-xs">CSV Rows</span>
                          <span className="font-bold text-lg">{csvImportResult.total_rows}</span>
                        </div>
                        <div className="bg-green-50 rounded p-2 text-center">
                          <span className="text-green-700 block text-xs">Homestays Updated</span>
                          <span className="font-bold text-lg text-green-600">{csvImportResult.success}</span>
                        </div>
                        <div className="bg-blue-50 rounded p-2 text-center">
                          <span className="text-blue-700 block text-xs">Images Uploaded</span>
                          <span className="font-bold text-lg text-blue-600">{csvImportResult.images_uploaded}</span>
                        </div>
                        <div className="bg-red-50 rounded p-2 text-center">
                          <span className="text-red-700 block text-xs">Failed</span>
                          <span className="font-bold text-lg text-red-600">{csvImportResult.failed}</span>
                        </div>
                      </div>

                      {csvImportResult.details?.length > 0 && (
                        <div className="mt-3 max-h-40 overflow-y-auto border rounded p-2 bg-gray-50">
                          {csvImportResult.details.map((detail, idx) => (
                            <div key={idx} className="flex items-center justify-between py-1.5 text-xs border-b last:border-0">
                              <span className="truncate flex-1 font-medium">{detail.name}</span>
                              <span className={`ml-2 px-2 py-0.5 rounded text-xs font-semibold ${
                                detail.status === 'success' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {detail.status === 'success' ? `+${detail.images_imported}` : detail.message || detail.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Close button */}
              <div className="flex justify-end border-t pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowFixImagesModal(false)}
                  disabled={fixImagesLoading || migrateR2Loading || csvImportLoading}
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Images Modal */}
        <Dialog open={showBulkImportModal} onOpenChange={(open) => {
          if (!bulkImportingImages) {
            setShowBulkImportModal(open);
            if (!open) setBulkImportResult(null);
          }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Import Images for Selected Homestays</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="rounded-lg p-4 bg-blue-50 border border-blue-200">
                <p className="text-sm text-blue-800">
                  This will fetch images from Google Places API for <strong>{selectedIds.length}</strong> selected homestays. Each homestay will get up to 10 images.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-medium">Image Source:</label>
                <select
                  value={useGoogleApi ? "google" : "dataforseo"}
                  onChange={(e) => setUseGoogleApi(e.target.value === "google")}
                  className="border rounded px-3 py-1.5 text-sm"
                  data-testid="bulk-import-source-select"
                >
                  <option value="google">Google Places API</option>
                  <option value="dataforseo">DataForSEO</option>
                </select>
              </div>

              {/* Selected homestays preview */}
              <div className="max-h-32 overflow-y-auto border rounded p-2 bg-gray-50">
                <p className="text-xs font-medium text-gray-500 mb-1">Selected homestays:</p>
                {filteredHomestays
                  .filter(h => selectedIds.includes(h.id))
                  .map(h => (
                    <div key={h.id} className="flex items-center justify-between py-1 text-xs border-b last:border-0">
                      <span className="truncate flex-1">{h.name}</span>
                      <span className="text-gray-400 ml-2">{(h.images || []).length} imgs</span>
                    </div>
                  ))}
              </div>

              {/* Results */}
              {bulkImportResult && (
                <div className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    {bulkImportResult.success > 0 ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                    )}
                    <span className="font-medium">Import Complete</span>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div className="bg-gray-50 rounded p-2 text-center">
                      <span className="text-gray-600 block text-xs">Processed</span>
                      <span className="font-bold text-lg">{bulkImportResult.total_to_process}</span>
                    </div>
                    <div className="bg-green-50 rounded p-2 text-center">
                      <span className="text-green-700 block text-xs">Success</span>
                      <span className="font-bold text-lg text-green-600">{bulkImportResult.success}</span>
                    </div>
                    <div className="bg-red-50 rounded p-2 text-center">
                      <span className="text-red-700 block text-xs">Failed</span>
                      <span className="font-bold text-lg text-red-600">{bulkImportResult.failed}</span>
                    </div>
                  </div>

                  {bulkImportResult.details?.length > 0 && (
                    <div className="max-h-40 overflow-y-auto border rounded p-2 bg-gray-50">
                      {bulkImportResult.details.map((detail, idx) => (
                        <div key={idx} className="flex items-center justify-between py-1.5 text-xs border-b last:border-0">
                          <span className="truncate flex-1 font-medium">{detail.name}</span>
                          <span className={`ml-2 px-2 py-0.5 rounded text-xs font-semibold ${
                            detail.status === 'success' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {detail.status === 'success' ? `+${detail.images_imported} imgs` : detail.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setShowBulkImportModal(false); setBulkImportResult(null); }}
                  disabled={bulkImportingImages}
                >
                  {bulkImportResult ? 'Close' : 'Cancel'}
                </Button>
                {!bulkImportResult && (
                  <Button
                    onClick={handleBulkImportImages}
                    disabled={bulkImportingImages || selectedIds.length === 0}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="bulk-import-confirm-btn"
                  >
                    {bulkImportingImages ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Import Images
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminHomestays;
